import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/dist/query/react";
import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";

import { API_ENDPOINT } from "../../config";
import { BatchResponse, BatchesResponse, Course, CourseFee } from "../../interfaces";
import { toastMessage } from "../../utils/toastError";
import { PaginationDto } from "../../interfaces/dto/default.dto";
import { CreateCourseDto, UpdateCourseDto } from "@/interfaces/dto/course.dto";

const COURSE_UPDATE_RATE_LIMIT_MESSAGE =
  "You have reached the update limit. Please try again after 5 minutes.";

function isHttpError(
  error: FetchBaseQueryError,
): error is Extract<FetchBaseQueryError, { status: number }> {
  return typeof error.status === "number";
}

export const batchApi = createApi({
  reducerPath: "batchApi",
  baseQuery: fetchBaseQuery({
    baseUrl: API_ENDPOINT + "/batch",
    credentials: "include",
  }),
  endpoints: (build) => ({
    getBatches: build.query<BatchesResponse, {}>({
      query: (params) => ({
        url: "/",
        method: "GET",
        params,
      }),
    }),
    downloadBatchReport: build.query<any, {}>({
      query: (params) => ({
        url: "/report",
        method: "GET",
        params,
      }),
    }),
    getBatch: build.query<BatchResponse, {}>({
      query: (id) => ({
        url: "/" + id,
        method: "GET",
      }),
    }),

    createCourse: build.mutation<Course, CreateCourseDto>({
      query: (body) => ({
        url: "/course",
        method: "POST",
        body,
      }),
      transformErrorResponse(baseQueryReturnValue) {
        toastMessage("error", baseQueryReturnValue.data);
        return Promise.resolve(baseQueryReturnValue.data);
      },
      transformResponse(baseQueryReturnValue: Course) {
        toastMessage("success", "Successfully created");
        return baseQueryReturnValue;
      },
    }),

    updateCourse: build.mutation<Course, UpdateCourseDto>({
      query: (body) => ({
        url: "/course",
        method: "PUT",
        body,
      }),
      transformErrorResponse(baseQueryReturnValue: FetchBaseQueryError) {
        if (isHttpError(baseQueryReturnValue) && baseQueryReturnValue.status === 429) {
          const data = baseQueryReturnValue.data as { message?: string } | undefined;
          const message = data?.message || COURSE_UPDATE_RATE_LIMIT_MESSAGE;
          toastMessage("error", message);
          return Promise.resolve(baseQueryReturnValue.data);
        }

        toastMessage("error", isHttpError(baseQueryReturnValue) ? baseQueryReturnValue.data : undefined);
        return Promise.resolve(isHttpError(baseQueryReturnValue) ? baseQueryReturnValue.data : baseQueryReturnValue);
      },
      transformResponse(baseQueryReturnValue: Course) {
        toastMessage("success", "Successfully updated");
        return baseQueryReturnValue;
      },
    }),

    deleteCourse: build.mutation<Course, { id: string }>({
      query: (body) => ({
        url: "/course",
        method: "DELETE",
        body,
      }),
      transformErrorResponse(baseQueryReturnValue) {
        toastMessage("error", baseQueryReturnValue.data);
        return Promise.resolve(baseQueryReturnValue.data);
      },
      transformResponse(baseQueryReturnValue: Course) {
        toastMessage("success", "Successfully deleted");
        return baseQueryReturnValue;
      },
    }),
    getCourses: build.query<{ entries: Course[]; total: number }, PaginationDto>({
      query: (params) => ({
        url: "/course",
        method: "GET",
        params,
      }),
    }),
  }),
});

export const {
  useGetBatchesQuery,
  useLazyGetBatchesQuery,
  useGetBatchQuery,
  useLazyGetBatchQuery,
  useLazyDownloadBatchReportQuery,
  useCreateCourseMutation,
  useGetCoursesQuery,
  useUpdateCourseMutation,
  useDeleteCourseMutation,
} = batchApi;
